import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Fade,
  Grid,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import 'swiper/swiper-bundle.css';
import Skeleton from '@material-ui/lab/Skeleton';
import DeleteIcon from '@material-ui/icons/Delete';
import RichEditor from '../../components/RichEditor/RichEditor';
import {
  DETAILS_COMPANY,
  EMAIL_COMPANY,
  FILE_ID_LOGO_COMPANY,
  INVOICE_ADDRESS_COMPANY,
  INVOICE_EMAIL_COMPANY,
  LOCATION_COMPANY,
  NAME_COMPANY,
  PUBLIC_URL_FILE,
  URL_COMPANY
} from '../../common/adapters';

const marginBottomSpacingInputs = 46;

const useStyles = makeStyles((theme) => ({
  root: {
    '& .MuiFormLabel-asterisk': {
      color: 'red'
    },
    '& .MuiSelect-outlined': {
      padding: '20px'
    },
    '& .MuiSelect-icon': {
      right: '7px'
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 3
    }
  },
  textField: {
    marginLeft: 0,
    marginRight: 0,
    marginBottom: marginBottomSpacingInputs
  },
  textEditorTitle: {
    marginBottom: '24px !important',
    marginTop: 20
  },
  textEditorInputArea: {
    padding: 0,
    marginLeft: 0,
    marginBottom: 40
  },
  headerCard: {
    textAlign: 'center'
  },
  logo: {
    width: '50px',
    height: '50px',
    borderRadius: '50%'
  },
  logoBtn: {
    padding: '8px',
    margin: '13px',
    textTransform: 'none'
  },
  textLogo: {
    marginBottom: marginBottomSpacingInputs
  },
  divider: {
    borderBottom: '1px solid rgba(0, 0, 0, 0.42)'
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    zIndex: 1,
    marginTop: '-62px',
    marginLeft: '-10px'
  },
  large: {
    width: theme.spacing(6),
    height: theme.spacing(6)
  },
  inputName: {
    textTransform: 'uppercase',
    marginBottom: 5
  },
  inputNameLogo: {
    marginBottom: 20
  }
}));

const COMPANY_FORM = 'companyForm';

const CompanyForm = ({
  onUploadFile,
  onDeleteLogo,
  formValues,
  handleChange,
  handleChangeCustomFieldName,
  hasError,
  file: fileLogo,
  isProcessingLogo,
  formErrors,
  companyLogoUrl
}) => {
  const classes = useStyles();
  const company = formValues;
  return (
    <>
      <Card className={classes.root}>
        <form autoComplete="off" noValidate>
          <CardHeader className={classes.headerCard} title="" />
          <CardContent>
            <Typography
              className={classes.inputName + ' ' + classes.inputNameLogo}
              variant="h5">
              COMPANY LOGO (.JPG OR .PNG, SQUARE OR ROUND)
            </Typography>
            <Grid
              spacing={2}
              alignItems="center"
              container
              direction="column"
              justifyContent="flex-start">
              <Grid item>
                <Grid
                  alignItems="center"
                  spacing={6}
                  container
                  direction="row"
                  justifyContent="flex-start">
                  <Grid item>
                    {isProcessingLogo ? (
                      <Skeleton variant="circle">
                        <Avatar className={classes.large} />
                      </Skeleton>
                    ) : (
                      <Avatar
                        data-test="logo-img-company-form"
                        className={classes.large}
                        src={companyLogoUrl || ''}
                      />
                    )}
                  </Grid>
                  {fileLogo && fileLogo[PUBLIC_URL_FILE] && (
                    <Grid item>
                      <Fade in>
                        <Button
                          color="secondary"
                          onClick={() => {
                            onDeleteLogo(fileLogo);
                          }}>
                          <DeleteIcon />
                        </Button>
                      </Fade>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                {(!fileLogo || !fileLogo[PUBLIC_URL_FILE]) && (
                  <Fade in>
                    <Button
                      data-test="upload-logo-button-company-form"
                      className={classes.logoBtn}
                      component="label"
                      variant="contained">
                      Upload your company logo
                      <input
                        data-test="upload-logo-field-company-form"
                        accept="image/*"
                        aria-label="Upaload logo"
                        name={FILE_ID_LOGO_COMPANY}
                        onInput={onUploadFile}
                        style={{ display: 'none' }}
                        type="file"
                      />
                    </Button>
                  </Fade>
                )}
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Typography
              className={classes.textLogo}
              color="textSecondary"
              variant="body2">
              It's highly recommended to use your Twitter or Facebook avatar.
              Optional — Your company logo will appear at the top of your
              listing.
            </Typography>
            <Typography className={classes.inputName} variant="h5">
              Company name
            </Typography>
            <TextField
              data-test="name-company-form"
              className={classes.textField}
              error={hasError(NAME_COMPANY)}
              fullWidth
              helperText={
                hasError(NAME_COMPANY)
                  ? formErrors[NAME_COMPANY][0]
                  : "Your company's brand/trade name: without Inc., Ltd., B.V., Pte., etc."
              }
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={NAME_COMPANY}
              onChange={handleChange}
              required
              value={company ? company[NAME_COMPANY] : ''}
              variant="outlined"
            />
            <Typography className={classes.inputName} variant="h5">
              Company location
            </Typography>
            <TextField
              data-test="location-company-form"
              className={classes.textField}
              error={hasError(LOCATION_COMPANY)}
              fullWidth
              helperText={
                hasError(LOCATION_COMPANY)
                  ? formErrors[LOCATION_COMPANY][0]
                  : 'Example: “Chicago, IL”,   “Stockholm, Sweden”'
              }
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={LOCATION_COMPANY}
              onChange={handleChange}
              required
              value={company ? company[LOCATION_COMPANY] : ''}
              variant="outlined"
            />
            <Typography className={classes.inputName} variant="h5">
              Company's Website URL
            </Typography>
            <TextField
              data-test="url-company-form"
              className={classes.textField}
              error={hasError(URL_COMPANY)}
              fullWidth
              helperText={
                hasError(URL_COMPANY)
                  ? formErrors[URL_COMPANY][0]
                  : 'Used to show the company web page on your company profile.'
              }
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={URL_COMPANY}
              onChange={handleChange}
              required
              value={company ? company[URL_COMPANY] : ''}
              variant="outlined"
            />
            <Typography className={classes.inputName} variant="h5">
              COMPANY EMAIL (STAYS PRIVATE, FOR INVOICE + EDIT LINK)
            </Typography>
            <TextField
              data-test="email-company-form"
              className={classes.textField}
              error={hasError(EMAIL_COMPANY)}
              fullWidth
              helperText={
                hasError(EMAIL_COMPANY)
                  ? formErrors[EMAIL_COMPANY][0]
                  : 'Make sure this email is accessible by you! We use this to send the invoice and edit link. We can not and do not manually resend it! If you use your company domain (same as company name), we will show a [ Verified ] tag on your job post.'
              }
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={EMAIL_COMPANY}
              onChange={handleChange}
              required
              type="Email"
              value={company ? company[EMAIL_COMPANY] : ''}
              variant="outlined"
            />
            <Typography
              className={classes.inputName + ' ' + classes.textEditorTitle}
              variant="h5">
              About your company
            </Typography>
            <Paper className={classes.textEditorInputArea}>
              <RichEditor
                name={DETAILS_COMPANY}
                onChange={(content) => {
                  handleChangeCustomFieldName(
                    content,
                    DETAILS_COMPANY,
                    COMPANY_FORM
                  );
                }}
                placeholder="Give more details about the company..."
                value={company ? company[DETAILS_COMPANY] : null}
              />
            </Paper>
            <Typography className={classes.inputName} variant="h5">
              INVOICE EMAIL (STAYS PRIVATE)
            </Typography>
            <TextField
              data-test="invoice-email-company-form"
              className={classes.textField}
              fullWidth
              helperText="We send a copy of the invoice and edit link to here too. You can write your finance department or accountant expenses email here so they get a copy of the invoice for your bookkeeping."
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={INVOICE_EMAIL_COMPANY}
              onChange={handleChange}
              value={company ? company[INVOICE_EMAIL_COMPANY] : ''}
              variant="outlined"
            />
            <Typography className={classes.inputName} variant="h5">
              INVOICE ADDRESS
            </Typography>
            <TextField
              data-test="invoice-address-company-form"
              className={classes.textField}
              fullWidth
              helperText="e.g. your company's full name and full invoice address, including building, street, city and country; also things like your VAT number, this is shown on the invoice."
              InputLabelProps={{
                shrink: true
              }}
              label=""
              margin="normal"
              name={INVOICE_ADDRESS_COMPANY}
              onChange={handleChange}
              value={company ? company[INVOICE_ADDRESS_COMPANY] : ''}
              variant="outlined"
            />
          </CardContent>
        </form>
      </Card>
    </>
  );
};

export default CompanyForm;
